import React, { useState, useRef, useEffect } from "react";
import {
  ImageOff,
  ZoomIn,
  X,
  ZoomOut,
  ZoomIn as ZoomInIcon,
} from "lucide-react";
import { PortfolioImage } from "@/hooks/useProfile";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { useValidImageUrl } from "./useValidImageUrl";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { convertSupabaseUrlToRenderUrl } from "@/utils/imageUtils";

interface ImageCarouselProps {
  images: PortfolioImage[];
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  handleImageError: (imageId: string, url?: string) => void;
  imageError: Record<string, boolean>;
  itemTitle: string;
}

const ImageCarousel = ({
  images,
  currentIndex,
  setCurrentIndex,
  handleImageError,
  imageError,
  itemTitle,
}: ImageCarouselProps) => {
  const { isValidImageUrl } = useValidImageUrl();
  const isMobile = useIsMobile();
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0, distance: 0 });
  const lastTapRef = useRef<number>(0);
  const doubleTapTimeoutRef = useRef<NodeJS.Timeout>();
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleZoomClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsZoomed(true);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClose = () => {
    setIsZoomed(false);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.5, 1));
  };

  const handleDoubleTap = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    if (scale === 1) {
      // Zoom in to 2x at tap position
      setScale(2);
      // Calculate new position to center on tap point
      const image = imageRef.current;
      if (image) {
        const scaledWidth = image.width * 2;
        const scaledHeight = image.height * 2;
        const maxX = (scaledWidth - container.clientWidth) / 2;
        const maxY = (scaledHeight - container.clientHeight) / 2;

        // Calculate the offset to center the tap point
        const offsetX = (x - container.clientWidth / 2) * 2;
        const offsetY = (y - container.clientHeight / 2) * 2;

        setPosition({
          x: Math.max(-maxX, Math.min(maxX, offsetX)),
          y: Math.max(-maxY, Math.min(maxY, offsetY)),
        });
      }
    } else {
      // Reset zoom
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const getDistance = (touches: TouchList | React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const now = Date.now();
      const lastTap = lastTapRef.current;

      if (now - lastTap < 300) {
        // Double tap detected
        clearTimeout(doubleTapTimeoutRef.current);
        handleDoubleTap(e.touches[0].clientX, e.touches[0].clientY);
      } else {
        // Single tap - start dragging
        setIsDragging(true);
        setTouchStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
          distance: 0,
        });
      }

      lastTapRef.current = now;
    } else if (e.touches.length === 2) {
      setTouchStart({
        x: 0,
        y: 0,
        distance: getDistance(e.touches),
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    if (e.touches.length === 1 && scale > 1) {
      const newX = e.touches[0].clientX - touchStart.x;
      const newY = e.touches[0].clientY - touchStart.y;

      const image = imageRef.current;
      const container = containerRef.current;
      if (image && container) {
        const scaledWidth = image.width * scale;
        const scaledHeight = image.height * scale;
        const maxX = (scaledWidth - container.clientWidth) / 2;
        const maxY = (scaledHeight - container.clientHeight) / 2;

        setPosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    } else if (e.touches.length === 2) {
      const newDistance = getDistance(e.touches);
      const delta = newDistance - touchStart.distance;

      if (Math.abs(delta) > 10) {
        const zoomFactor = delta > 0 ? 0.1 : -0.1;
        setScale((prev) => Math.max(1, Math.min(3, prev + zoomFactor)));
        setTouchStart((prev) => ({ ...prev, distance: newDistance }));
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      const image = imageRef.current;
      const container = containerRef.current;
      if (image && container) {
        const scaledWidth = image.width * scale;
        const scaledHeight = image.height * scale;
        const maxX = (scaledWidth - container.clientWidth) / 2;
        const maxY = (scaledHeight - container.clientHeight) / 2;

        setPosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();

    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) return;

    // Get mouse position relative to container
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate mouse position relative to image center
    const imageCenterX = container.clientWidth / 2;
    const imageCenterY = container.clientHeight / 2;
    const mouseOffsetX = mouseX - imageCenterX;
    const mouseOffsetY = mouseY - imageCenterY;

    // Calculate zoom factor based on wheel delta
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(1, Math.min(3, scale * zoomFactor));

    // Calculate new position to zoom towards mouse
    if (newScale !== scale) {
      const scaleChange = newScale - scale;
      const newX = position.x - (mouseOffsetX * scaleChange) / newScale;
      const newY = position.y - (mouseOffsetY * scaleChange) / newScale;

      // Calculate boundaries
      const scaledWidth = image.width * newScale;
      const scaledHeight = image.height * newScale;
      const maxX = (scaledWidth - container.clientWidth) / 2;
      const maxY = (scaledHeight - container.clientHeight) / 2;

      // Update scale and position
      setScale(newScale);
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY)),
      });
    }
  };

  // Reset position when scale changes
  useEffect(() => {
    if (scale === 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  // Add keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isZoomed) return;

      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "+") {
        handleZoomIn();
      } else if (e.key === "-") {
        handleZoomOut();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isZoomed]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (doubleTapTimeoutRef.current) {
        clearTimeout(doubleTapTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      {/* Image count indicator */}
      <div className="absolute top-3 right-3 z-20 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full">
        {currentIndex + 1} / {images.length}
      </div>

      <Carousel
        className="w-full"
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
      >
        <CarouselContent>
          {images.map((image, index) => {
            const isValid = isValidImageUrl(image.url);
            return (
              <CarouselItem key={image.id || `image-${index}`}>
                <AspectRatio
                  ratio={isMobile ? 4 / 3 : 16 / 10}
                  className="bg-muted"
                >
                  <div
                    className={`relative w-full h-full ${
                      isMobile
                        ? "min-h-[200px]"
                        : "min-h-[300px] md:min-h-[500px]"
                    } flex items-center justify-center rounded-md overflow-hidden`}
                  >
                    {isValid && !imageError[image.id] ? (
                      isMobile ? (
                        <div
                          className="relative w-full h-full group cursor-zoom-in"
                          onClick={handleZoomClick}
                          role="button"
                          tabIndex={0}
                          aria-label="Zoom image"
                        >
                          <img
                            src={convertSupabaseUrlToRenderUrl(image.url, 50)}
                            alt={`${itemTitle} - image ${index + 1}`}
                            className="w-full h-full object-contain transition-all duration-300 group-hover:scale-[1.01]"
                            onError={() =>
                              handleImageError(image.id, image.url)
                            }
                            loading="lazy"
                            decoding="async"
                            {...(image.url.toLowerCase().endsWith(".gif") && {
                              style: { imageRendering: "auto" },
                              "data-animated": "true",
                            })}
                          />
                        </div>
                      ) : (
                        <div className="relative w-full h-full group">
                          <img
                            src={convertSupabaseUrlToRenderUrl(image.url, 50)}
                            alt={`${itemTitle} - image ${index + 1}`}
                            className="w-full h-full object-contain transition-all duration-300 group-hover:scale-[1.01]"
                            onError={() =>
                              handleImageError(image.id, image.url)
                            }
                            loading="lazy"
                            decoding="async"
                            {...(image.url.toLowerCase().endsWith(".gif") && {
                              style: { imageRendering: "auto" },
                              "data-animated": "true",
                            })}
                          />
                          <div
                            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-zoom-in"
                            onClick={handleZoomClick}
                          >
                            <div className="bg-black/30 rounded-full p-2">
                              <ZoomIn className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full bg-muted">
                        <ImageOff className="h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Image unavailable
                        </p>
                      </div>
                    )}
                  </div>
                </AspectRatio>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        <CarouselPrevious
          className={`
            absolute left-1 md:left-2 top-1/2 -translate-y-1/2 z-10 
            ${isMobile ? "h-8 w-8" : "h-10 w-10"}
            bg-background/90
            shadow-md
          `}
        />
        <CarouselNext
          className={`
            absolute right-1 md:right-2 top-1/2 -translate-y-1/2 z-10 
            ${isMobile ? "h-8 w-8" : "h-10 w-10"}
            bg-background/90
            shadow-md
          `}
        />
      </Carousel>

      {/* Zoom Modal */}
      <Dialog open={isZoomed} onOpenChange={handleClose}>
        <DialogContent className="max-w-full sm:max-w-[95vw] max-h-screen sm:max-h-[95vh] p-0 bg-black/90 border-none flex flex-col overflow-y-auto justify-center items-center">
          <div
            ref={containerRef}
            className="relative w-full flex-1 flex items-center justify-center overflow-auto min-h-[300px] sm:min-h-[300px] px-0 py-0"
            style={{ minHeight: "300px", minWidth: 0 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {images[currentIndex] && (
              <>
                <img
                  ref={imageRef}
                  src={convertSupabaseUrlToRenderUrl(
                    images[currentIndex].url,
                    50
                  )}
                  alt={`${itemTitle} - zoomed view`}
                  className="object-contain transition-transform duration-200 select-none w-full h-auto max-h-[70vh] sm:max-h-[90vh] sm:max-w-[95vw] mx-auto shadow-lg bg-white"
                  style={{
                    transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                    cursor:
                      scale > 1
                        ? isDragging
                          ? "grabbing"
                          : "grab"
                        : "default",
                    touchAction: "none",
                  }}
                  draggable={false}
                />

                {/* Zoom Controls - compact, floating, centered on desktop */}
                <div
                  className={`absolute left-1/2 -translate-x-1/2 bottom-2 sm:bottom-6 flex items-center justify-center gap-2
                    ${
                      isMobile
                        ? "bg-black/70 rounded-xl px-3 py-2 shadow-md"
                        : "bg-black/80 rounded-full px-4 py-3 shadow-lg"
                    }
                    z-30 max-w-full sm:max-w-[320px] w-auto`}
                  style={isMobile ? { minHeight: 0, marginBottom: 8 } : {}}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`text-white hover:bg-white/20 ${
                      isMobile ? "h-8 w-8" : "h-8 w-8"
                    }`}
                    onClick={handleZoomOut}
                    disabled={scale <= 1}
                  >
                    <ZoomOut className={isMobile ? "h-5 w-5" : "h-4 w-4"} />
                  </Button>
                  <span
                    className={`text-white ${
                      isMobile ? "text-base" : "text-base"
                    } min-w-[2.5rem] text-center select-none`}
                  >
                    {Math.round(scale * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`text-white hover:bg-white/20 ${
                      isMobile ? "h-8 w-8" : "h-8 w-8"
                    }`}
                    onClick={handleZoomIn}
                    disabled={scale >= 3}
                  >
                    <ZoomInIcon className={isMobile ? "h-5 w-5" : "h-4 w-4"} />
                  </Button>
                </div>

                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="fixed sm:absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/70 hover:bg-black/90 text-white rounded-full p-2 transition-colors z-40"
                >
                  <X className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageCarousel;
